import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import MapToolRotation from './MapToolRotation'
import MapToolCurrentLocation from './MapToolCurrentLocation'
import BaseMapToggle from './MapToolBaseMapToggle'
import MapToolZoomIn from './MapToolZoomIn'
import MapToolZoomOut from './MapToolZoomOut'
import MapToolSpeedDial from './MapToolSpeedDial'
import MapResultWeb from './MapResultWeb'
import Paper from '@mui/material/Paper'
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow'
import useMapStore from '../../stores/useMapStore'
import { observer } from 'mobx-react-lite'
import { ReactComponent as SignalGoodIcon } from '../../icon/signal_good.svg'
import { ReactComponent as SignalModerateIcon } from '../../icon/signal_moderate.svg'
import { ReactComponent as SignalBadIcon } from '../../icon/signal_bad.svg'
import { ReactComponent as SignalNormalIcon } from '../../icon/signal_normal.svg'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { probabilityRatio } from '../../constant/Constant'

const windowHeightMobile = 650

type MapResultToggleWebProps = {
    data?: SearchDataType | null
    disabledCurrentLocation?: boolean
    expanded: boolean
    onClickResult: (item: StationType) => void
    onClickShowExpand: () => void
    onClickHideExpand: () => void
    onBaseMapSelected?: (name?: string) => void
    onClickRotation: () => void
    onClickCurrentLocation: () => void
    onClickZoomIn: () => void
    onClickZoomOut: () => void
    resetSearchData: () => void
}
const MapResultToggleWeb = observer((props: MapResultToggleWebProps) => {
    const {
        data,
        disabledCurrentLocation,
        expanded,
        onClickResult,
        onClickShowExpand,
        onClickHideExpand,
        onBaseMapSelected,
        onClickRotation,
        onClickCurrentLocation,
        onClickZoomIn,
        onClickZoomOut,
        resetSearchData,
    } = props

    useEffect(() => {
        const handleResize = () => {
            // if (openBasemapGallery){
            //     setOpenBasemapGallery(false)
            // }
            setOpenBasemapGallery(false)
            setWindowDimensions(getWindowDimensions())
        }

        globalThis.addEventListener('resize', handleResize)
        return () => globalThis.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        setOpenBasemapGallery(false)
        if (data && data.stations?.length) {
            onClickShowExpand()
        } else {
            onClickHideExpand()
        }
    }, [data])

    useEffect(() => {
        setShowResult(expanded)
    }, [expanded])

    const onClickBaseMap = () => {
        setOpenBasemapGallery((prev) => !prev)
    }

    const onClickSpeedDial = (isOpen: boolean) => {
        setOpenSpeedDial(isOpen)
    }

    const closeBasemapGallery = (flag: boolean) => {
        setOpenBasemapGallery(flag)
    }

    const onHilightSelectStation = (data: number) => {
        mapStore.hilightSelectStation(data)
    }

    const getSignalStrength = (signalStrength?: number) => {
        if (signalStrength)
            if (signalStrength >= probabilityRatio.good)
                return {
                    icon: <SignalGoodIcon />,
                    message: `คาดว่า${t('LABEL.MAP.MAP_RESULT.GOOD_RECEPTION')}ได้ดี`,
                    width: '170px',
                }
            else if (signalStrength >= probabilityRatio.average)
                return {
                    icon: <SignalModerateIcon />,
                    message: `คาดว่า${t('LABEL.MAP.MAP_RESULT.GOOD_RECEPTION')}ได้`,
                    width: '160px',
                }
            else
                return {
                    icon: <SignalBadIcon />,
                    message: `คาดว่าพอจะ${t('LABEL.MAP.MAP_RESULT.GOOD_RECEPTION')}ได้`,
                    width: '195px',
                }
        else return { icon: <SignalNormalIcon style={{ fill: '#333333' }} />, message: '', width: '' }
    }

    const getWindowDimensions = () => {
        const { innerWidth: width, innerHeight: height } = window
        return { width, height }
    }

    const [showResult, setShowResult] = useState(false)
    const [openSpeedDial, setOpenSpeedDial] = useState(false)
    const [openBasemapGallery, setOpenBasemapGallery] = useState(false)
    const [openTooltipSignalStrength, setOpenTooltipSignalStrength] = useState(false)
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())

    const classes = useStyles()
    const { t } = useTranslation()
    const mapStore = useMapStore()

    const onClickTooltipSignalStrength = () => setOpenTooltipSignalStrength(!openTooltipSignalStrength)

    return (
        <div className={clsx(classes.slide, { [classes.slideIn]: showResult, [classes.slideOut]: !showResult })}>
            <div className={classes.elementTools}>
                <BaseMapToggle
                    onBaseMapSelected={onBaseMapSelected}
                    openBasemapGallery={openBasemapGallery}
                    onClickBaseMap={onClickBaseMap}
                    closeBasemapGallery={closeBasemapGallery}
                    positionTop={109}
                />

                {data?.stations && data.stations.length > 0 && (
                    // <Tooltip
                    //     title={getSignalStrength(data?.probability).message}
                    //     placement="right"
                    //     classes={{ tooltip: classes.tooltipSignalStrength }}
                    // >
                    <Tooltip
                        open={openTooltipSignalStrength}
                        title={getSignalStrength(data?.probability).message}
                        placement="right"
                        onClick={onClickTooltipSignalStrength}
                        onMouseOver={() => setOpenTooltipSignalStrength(true)}
                        onMouseLeave={() => setOpenTooltipSignalStrength(false)}
                        classes={{ tooltip: classes.tooltipSignalStrength }}
                    >
                        <Paper className={classes.reception}>
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                {getSignalStrength(data?.probability).icon}
                            </Stack>
                        </Paper>
                    </Tooltip>
                )}

                <Box className={classes.mapTool}>
                    {windowDimensions.height >= windowHeightMobile ? (
                        <>
                            <MapToolRotation onClickRotation={onClickRotation} />
                            <MapToolCurrentLocation
                                disabled={disabledCurrentLocation}
                                onClickCurrentLocation={onClickCurrentLocation}
                            />
                            <MapToolZoomIn onClickZoomIn={onClickZoomIn} />
                            <MapToolZoomOut onClickZoomOut={onClickZoomOut} />
                        </>
                    ) : (
                        <MapToolSpeedDial
                            platform={'web'}
                            isOpen={openSpeedDial}
                            isShowResult={showResult}
                            disabledCurrentLocation={disabledCurrentLocation}
                            onClickCurrentLocation={onClickCurrentLocation}
                            onClickSpeedDial={onClickSpeedDial}
                            onClickRotation={onClickRotation}
                            onClickZoomOut={onClickZoomOut}
                            onClickZoomIn={onClickZoomIn}
                        />
                    )}
                </Box>

                {data?.stations && data.stations.length > 0 && !openSpeedDial && (
                    <>
                        {showResult ? (
                            <Paper
                                className={
                                    windowDimensions.height >= windowHeightMobile
                                        ? classes.buttonHideResult
                                        : classes.buttonHideResultMobile
                                }
                                component="div"
                                onClick={() => {
                                    showResult ? onClickHideExpand() : onClickShowExpand()
                                    setOpenBasemapGallery(false)
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                    <Typography className={classes.textHideResult}>
                                        {t('BUTTON.HIDE_RESULT')}
                                    </Typography>
                                    <DoubleArrowIcon style={{ transform: 'rotate(90deg)', fontSize: '18px' }} />
                                </Stack>
                            </Paper>
                        ) : (
                            <Paper
                                className={
                                    windowDimensions.height >= windowHeightMobile
                                        ? classes.buttonShowResult
                                        : classes.buttonShowResultMobile
                                }
                                component="div"
                                onClick={() => {
                                    showResult ? onClickHideExpand() : onClickShowExpand()
                                    setOpenBasemapGallery(false)
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                    <Typography className={classes.textShowResult}>
                                        {t('BUTTON.SHOW_RESULT')}
                                    </Typography>
                                    <DoubleArrowIcon
                                        style={{ transform: 'rotate(-90deg)', color: 'white', fontSize: '18px' }}
                                    />
                                </Stack>
                            </Paper>
                        )}
                        <Paper
                            className={clsx(
                                windowDimensions.height >= windowHeightMobile
                                    ? classes.btnCloseHideResult
                                    : classes.btnCloseHideResultMobile,
                                {
                                    [windowDimensions.height >= windowHeightMobile
                                        ? classes.btnCloseShowResult
                                        : classes.btnCloseShowResultMobile]: showResult,
                                },
                            )}
                            component="div"
                        >
                            <Tooltip title={t('BUTTON.CLOSE') ?? ''} placement="bottom">
                                <IconButton aria-label="close" onClick={() => resetSearchData()}>
                                    <CloseIcon sx={{ color: '#fff' }} />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    </>
                )}
            </div>
            <div className={classes.elementResult}>
                <MapResultWeb
                    data={data}
                    stationNumber={mapStore.selectStationNumber}
                    onClickResult={onClickResult}
                    onHilightSelectStation={onHilightSelectStation}
                    resetSearchData={resetSearchData}
                />
            </div>
        </div>
    )
})

export default MapResultToggleWeb

const useStyles = makeStyles((theme: Theme) => ({
    slide: {
        position: 'fixed',
        width: '100%',
        height: 'auto',
    },
    slideIn: {
        animation: '$slideIn 0.5s forwards',
    },
    slideOut: {
        animation: '$slideOut 0.1s forwards',
    },
    '@keyframes slideIn': {
        '0%': {
            transform: 'translateY(0%)',
        },
        '100%': {
            transform: 'translateY(-50%)',
        },
    },
    '@keyframes slideOut': {
        '100%': {
            transform: 'translateY(0%)',
        },
    },
    elementTools: {
        display: 'flex',
        justifyContent: 'end',
        height: '43px',
    },
    elementResult: {
        display: 'inline-block',
        width: '100%',
        height: '415px',
        backgroundColor: 'rgba(255, 255, 255, 0)',
    },
    mapTool: {
        position: 'absolute',
        right: '16px',
        top: '-228px',
    },
    textResult: {
        fontSize: '15px !important',
        lineHeight: '23px !important',
        paddingTop: '12px',
        paddingBottom: '9.72px',
    },
    reception: {
        padding: theme.spacing(1),
        backgroundColor: '#fff',
        position: 'absolute',
        left: '16px',
        bottom: '430px',
        borderRadius: '5px',
    },
    resetResult: {
        padding: theme.spacing(1),
        backgroundColor: '#fff',
        position: 'absolute',
        bottom: '430px',
        borderRadius: '5px',
    },
    buttonHideResult: {
        padding: theme.spacing(1),
        backgroundColor: '#fff !important',
        position: 'absolute',
        right: '70px',
        bottom: '430px',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    buttonHideResultMobile: {
        padding: theme.spacing(1),
        backgroundColor: '#fff !important',
        position: 'absolute',
        right: '115px',
        bottom: '430px',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    buttonShowResult: {
        padding: theme.spacing(1),
        backgroundColor: `${theme.palette.primary.main} !important`,
        position: 'absolute',
        right: '115px',
        bottom: '493px',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    buttonShowResultMobile: {
        padding: theme.spacing(1),
        backgroundColor: `${theme.palette.primary.main} !important`,
        position: 'absolute',
        right: '115px',
        bottom: '493px',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    textHideResult: {
        color: '#333333',
        fontSize: '14px !important',
    },
    textShowResult: {
        color: '#fff',
        fontSize: '14px !important',
    },
    btnCloseHideResult: {
        backgroundColor: 'red !important',
        position: 'absolute',
        right: '70px',
        bottom: '493px',
        borderRadius: '50% !important',
    },
    btnCloseHideResultMobile: {
        backgroundColor: 'red !important',
        position: 'absolute',
        right: '70px',
        bottom: '493px',
        borderRadius: '50% !important',
    },
    btnCloseShowResult: {
        backgroundColor: 'red !important',
        position: 'absolute',
        right: '20px',
        bottom: '430px',
        borderRadius: '50% !important',
    },
    btnCloseShowResultMobile: {
        backgroundColor: 'red !important',
        position: 'absolute',
        right: '70px',
        bottom: '430px',
        borderRadius: '50% !important',
    },
    tooltipSignalStrength: {
        margin: '10px !important',
        fontSize: '16px !important',
    },
}))
